import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { isWorkspacePath } from './siteHosts';

export const DEFAULT_DEPARTMENT_SLUG = 'grocery';

/** First URL segment for storefront catalog when no department prefix is used. */
const GLOBAL_CATALOG_ROOTS = new Set(['products', 'packages']);

export function normalizeDepartmentSlug(input?: string | null): string {
  const slug = String(input || '')
    .trim()
    .toLowerCase();
  return slug || DEFAULT_DEPARTMENT_SLUG;
}

/**
 * Path under `/products` or `/packages` (global) or `/{dept}/products` etc., without leading department.
 * Examples: `/products`, `/products/cuid`, `/packages`, `/packages/foo`
 */
export function storefrontCatalogPathSuffix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const [a, b, c] = [segments[0], segments[1], segments[2]];
  if (!a) return '';
  if (a === 'products') {
    return b ? `/products/${b}` : '/products';
  }
  if (a === 'packages') {
    return b ? `/packages/${b}` : '/packages';
  }
  if (b === 'products') {
    return c ? `/products/${c}` : '/products';
  }
  if (b === 'packages') {
    return c ? `/packages/${c}` : '/packages';
  }
  return '';
}

export function departmentPath(
  departmentSlug: string | null | undefined,
  suffix = '',
): string {
  const cleanSuffix = suffix
    ? suffix.startsWith('/')
      ? suffix
      : `/${suffix}`
    : '';
  if (departmentSlug == null || String(departmentSlug).trim() === '') {
    return cleanSuffix || '/';
  }
  const slug = normalizeDepartmentSlug(departmentSlug);
  return `/${slug}${cleanSuffix}`;
}

/**
 * Persist the active workspace department scope and notify listeners.
 * Workspace pages read this from localStorage and re-fetch when it changes.
 * Pass `'__all__'` (or omit) to clear the filter.
 */
export function setWorkspaceDepartmentScope(
  departmentSlug: string | null | undefined,
): void {
  if (typeof window === 'undefined') return;
  const scope =
    departmentSlug == null || String(departmentSlug).trim() === ''
      ? '__all__'
      : normalizeDepartmentSlug(departmentSlug);
  try {
    window.localStorage.setItem('workspace_department_scope', scope);
  } catch {
    // ignore quota/availability issues
  }
  window.dispatchEvent(
    new CustomEvent('workspace-department-scope:change', {
      detail: { scope },
    }),
  );
}

/**
 * Storefront department scope for API calls and links.
 * Returns `null` when browsing the global catalog (`/products`, `/packages`, …) so callers omit `departmentSlug` and load all departments.
 */
export function useDepartmentSlug(): string | null {
  const params = useParams<{ departmentSlug?: string }>();
  const location = useLocation();
  const [workspaceScope, setWorkspaceScope] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return String(window.localStorage.getItem('workspace_department_scope') || '');
  });

  useEffect(() => {
    const handleScopeChange = () => {
      setWorkspaceScope(
        String(window.localStorage.getItem('workspace_department_scope') || ''),
      );
    };
    window.addEventListener(
      'workspace-department-scope:change',
      handleScopeChange as EventListener,
    );
    window.addEventListener('storage', handleScopeChange);
    return () => {
      window.removeEventListener(
        'workspace-department-scope:change',
        handleScopeChange as EventListener,
      );
      window.removeEventListener('storage', handleScopeChange);
    };
  }, []);

  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const first = segments[0] || '';
    if (GLOBAL_CATALOG_ROOTS.has(first)) {
      return null;
    }

    if (params.departmentSlug) {
      return normalizeDepartmentSlug(params.departmentSlug);
    }
    const isWorkspace = isWorkspacePath(location.pathname);
    if (!isWorkspace) {
      return DEFAULT_DEPARTMENT_SLUG;
    }
    const scope = String(workspaceScope || '').trim().toLowerCase();
    if (!scope || scope === '__all__') {
      return DEFAULT_DEPARTMENT_SLUG;
    }
    return normalizeDepartmentSlug(scope);
  }, [location.pathname, params.departmentSlug, workspaceScope]);
}
